import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { apiConfigs } from '../../../../api/APIConfigs';
import { BibleScripture } from '../../../../api/models/BibleVerse';
import {
  BibleRangePayload,
  generateBibleRangePayloadFromScriptureIDs,
  normalizeScriptureTupleWithBooksCatalog,
} from '../../../../util/bible-study-module';
import { useRequest } from '../../../../util/use-request';
import { SizedBox } from '../../../core/SizedBox';
import { colors, Description, H2 } from '../../../core/Styles';

export interface ScriptureTextProps {
  /** A.K.A. `translationID` in the API */
  version: string;
  /** Example: ['Genesis', '1', '1', '-', 'Genesis', '1', '5'] */
  scriptureIds: string[];
}

export const ScriptureText = (props: ScriptureTextProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [scriptures, setScriptures] = useState<BibleScripture[]>([]);
  const [range, setRange] = useState<BibleRangePayload | null>(null);

  const request = useRequest();

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const versions = await request<Array<{ id: string; value: string; name: string }>>(
          apiConfigs.getBibleVersions()
        );
        const matchedVersion = (versions || []).find(v => v.id === props.version || v.value === props.version);
        const translationId = matchedVersion?.id || props.version;

        let books: Array<{ id: string }> = [];
        try {
          books = await request<Array<{ id: string }>>(apiConfigs.getBibleBooks(translationId));
        } catch {
          toast.error('Failed to get books');
        }
        const normalizedIds = normalizeScriptureTupleWithBooksCatalog(props.scriptureIds, books || []);
        const range = generateBibleRangePayloadFromScriptureIDs(normalizedIds);
        const scripts = await request<BibleScripture[]>(apiConfigs.getBibleRange({ ...range, version: translationId }));
        setRange(range);
        setScriptures(scripts);
      } catch (e: any) {
        toast.error(e.message || 'Failed to load scripture!');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [props.scriptureIds, props.version, request]);

  return (
    <Text>
      {isLoading ? (
        <Description>Loading scriptures...</Description>
      ) : (
        <>
          <H2>
            {range?.book} {range?.fromChapter}
            {`:${range?.fromVerse}`}
            {`-${range?.toChapter}`}
            {`:${range?.toVerse}`}
          </H2>
          <SizedBox height={16} />
          {!!scriptures.length ? (
            <List>
              {scriptures.map(scripture => (
                <Description key={scripture.id}>
                  <sup style={{ fontSize: 10 }}>{scripture.verse}</sup> {scripture.text}
                </Description>
              ))}
            </List>
          ) : (
            <Description color={colors.grey}>Scripture</Description>
          )}
        </>
      )}
    </Text>
  );
};

const Text = styled.div`
  border-radius: 10px;
  border: 1px solid #e2e7ea;
  outline: none;
  padding: 20px;
`;

const List = styled.div`
  & > :not(:last-child) {
    margin-bottom: 16px;
  }
`;
